import {
    Schedule,
    ChannelFeedResponse,
    SortedChannels,
    CategoryMap,
    ChannelFeedChannel,
    ChannelGuideTaxonomy,
} from './interfaces';
import { type ChannelButton } from './ChannelHeader';

// Keep early network priority on the initially visible channel logos only.
const PRIORITY_CHANNEL_IMAGE_COUNT = 5;

export function getChannelImageLoadingProps(index: number) {
    const shouldPrioritize = index < PRIORITY_CHANNEL_IMAGE_COUNT;

    return {
        loadingStrategy: shouldPrioritize ? ('eager' as const) : ('lazy' as const),
        fetchPriority: shouldPrioritize ? ('high' as const) : ('auto' as const),
    };
}

export const hiddenChannelImageLoadingProps = {
    loadingStrategy: 'lazy' as const,
    fetchPriority: 'auto' as const,
};

export const categoryMap: CategoryMap = {
    Music: 'Music',
    Sports: 'Sports',
    'Talk & Entertainment': 'Talk',
    'News & Issues': 'News',
    Comedy: 'Talk',
    'Howard Stern': 'Talk',
    Holiday: 'Holiday',
};

export const XtraChannels = [
    'Top 100',
    'New Music Discovery',
    'Music Mash',
    "Today's Hits",
    'Party',
    'Workout',
    'The 80s Decade',
    'Cool Down',
    'The 70s Decade',
    'BBQ',
    'The 90s Decade',
    'Instrumentals',
    'Just Music',
    'Deep Cuts',
];

// Channel Guide-only overrides: content editors can point a genre at a different
// category (e.g. folding Comedy/Howard Stern under Talk & Entertainment) and give a
// category a shorter tab label, without affecting how Genre/Category are used elsewhere.
export function getGenreCategory(genre: SiteGenre): SiteCategory | undefined {
    return genre.fields.channel_guide_category || genre.fields.category;
}

export function getCategoryDisplayName(category: SiteCategory): string {
    return category.fields.short_display_name || category.fields.display_name;
}

export function deriveChannelGuideTaxonomy(genres: SiteGenre[]): ChannelGuideTaxonomy {
    const categories: SiteCategory[] = [];
    const seenCategoryKeys = new Set<string>();
    for (const genre of genres) {
        const category = getGenreCategory(genre);
        if (category?.fields.key && !seenCategoryKeys.has(category.fields.key)) {
            seenCategoryKeys.add(category.fields.key);
            categories.push(category);
        }
    }

    return {
        categories,
        genres,
        genreOrder: genres.map((genre) => genre.fields.key),
    };
}

export function getChannelGuideTaxonomy(fields: SiteChannelGuide['fields']): ChannelGuideTaxonomy | undefined {
    if (!fields.genre_order?.length) {
        return undefined;
    }

    return deriveChannelGuideTaxonomy(fields.genre_order);
}

function formatHoursAndMinutes(milliseconds: number, casing: 'upper' | 'lower'): string {
    const hours = Math.floor(milliseconds / (1000 * 60 * 60));
    const minutes = Math.floor((milliseconds % (1000 * 60 * 60)) / (1000 * 60));

    if (casing === 'upper') {
        return `${hours ? `${hours}H ` : ''}${minutes ? `${minutes}M` : ''}`;
    }

    return `${hours}h ${minutes}m`;
}

export function isLiveWithTimeLeft(timeRanges?: Schedule[], now = Date.now()) {
    if (!timeRanges) {
        return { isLive: false, timeLeft: '' };
    }
    const currentTime = new Date(now);

    for (const range of timeRanges) {
        const startDate = new Date(range.startTime);
        const endDate = new Date(range.endTime);

        if (currentTime >= startDate && currentTime <= endDate) {
            const timeLeftMs = endDate.getTime() - currentTime.getTime();

            return {
                isLive: true,
                timeLeft: formatHoursAndMinutes(timeLeftMs, 'lower'),
                liveShowID: range.showId,
            };
        }
    }

    return {
        isLive: false,
        timeLeft: '',
        liveShowID: '',
    };
}

export function sortChannelsByChannelNumber(data: ChannelFeedResponse) {
    return data?.channels?.sort((a, b) => a.siriusChannelNumber - b.siriusChannelNumber);
}

export function groupChannelIntoCategories(
    data: ChannelFeedChannel[],
    xtraChannels: string[],
    categoryMap: CategoryMap,
    taxonomy?: ChannelGuideTaxonomy,
) {
    const sortedChannels: SortedChannels = {};
    const categoryByKey = taxonomy ? new Map(taxonomy.categories.map((item) => [item.fields.key, item])) : undefined;
    const genreByKey = taxonomy ? new Map(taxonomy.genres.map((item) => [item.fields.key, item])) : undefined;

    data?.forEach((channel) => {
        const contentfulGenre = genreByKey?.get(channel.genre);
        const genreCategory = contentfulGenre && getGenreCategory(contentfulGenre);
        const contentfulCategory = genreCategory ? categoryByKey?.get(genreCategory.fields.key) : undefined;
        const category = taxonomy
            ? contentfulCategory && getCategoryDisplayName(contentfulCategory)
            : categoryMap[channel.categoryTitle as keyof CategoryMap];
        const genre = taxonomy ? contentfulGenre?.fields.display_name : channel.genreTitle;

        if (!category || !genre) {
            return;
        }

        if (!sortedChannels[category]) {
            sortedChannels[category] = { All: [], AllX: [] };
        }

        if (!sortedChannels[category][genre]) {
            sortedChannels[category][genre] = [];
        }

        sortedChannels[category].All.push(channel);
        sortedChannels[category][genre].push(channel);

        const isXtraChannel = taxonomy
            ? channel.xtra_channel || channel.channel_type === 'Xtra'
            : xtraChannels.includes(channel.genreTitle);
        if (isXtraChannel) {
            sortedChannels[category].AllX.push(channel);
        }
    });

    return sortedChannels;
}

export function sortChannels(
    data: ChannelFeedResponse,
    xtraChannels: string[],
    categoryMap: CategoryMap,
    taxonomy?: ChannelGuideTaxonomy,
) {
    const sortedByChannelNumber = sortChannelsByChannelNumber(data);
    const groupedChannels = groupChannelIntoCategories(sortedByChannelNumber, xtraChannels, categoryMap, taxonomy);

    return { groupedChannels, sortedByChannelNumber };
}

export function millisecondsToHoursAndMinutes(milliseconds: number) {
    return formatHoursAndMinutes(milliseconds, 'upper');
}

export function buildChannelNavigationData(sourceObj: SortedChannels | null, taxonomy?: ChannelGuideTaxonomy) {
    if (taxonomy) {
        const categoryTitles = taxonomy.categories
            .map((category) => getCategoryDisplayName(category))
            .filter((title) => !sourceObj || sourceObj[title]);
        const channelGuideBody = Object.fromEntries(categoryTitles.map((title) => [title, title]));
        const channelButtons: ChannelButton[] = [{ title: 'All' }, ...categoryTitles.map((title) => ({ title }))];

        return { channelGuideBody, channelButtons };
    }

    if (!sourceObj) {
        return {
            channelButtons: [
                { title: 'All' },
                { title: 'Music' },
                { title: 'Sports' },
                { title: 'Talk' },
                { title: 'News' },
            ],
            channelGuideBody: {
                Music: 'Music',
                Sports: 'Sports',
                Talk: 'Talk',
                News: 'News',
            },
        };
    }

    type Keys = keyof SortedChannels & string;
    const keys = Object.keys(sourceObj) as Keys[];
    const channelGuideBody: Record<Keys, Keys> = keys.reduce(
        (acc, key) => {
            acc[key] = key;
            return acc;
        },
        {} as Record<Keys, Keys>,
    );

    const createdChannelButtons: ChannelButton[] = keys.map((key) => ({
        title: key,
    }));
    const channelButtons = [{ title: 'All' }, ...createdChannelButtons];

    const holidayIndex = channelButtons.findIndex((btn) => btn.title === 'Holiday');
    if (holidayIndex > -1) {
        const [holidayItem] = channelButtons.splice(holidayIndex, 1);
        channelButtons.splice(1, 0, holidayItem);
    }

    return { channelGuideBody, channelButtons };
}
